import { html, nothing, css, LitElement } from 'lit'
import { customElement, state, property } from 'lit/decorators.js'
import type { AnnotatedImageAttributes } from 'zaptic-annotated-image'
import classNames from 'classnames'

const MAX_RETRIES = 4

@customElement('zaptic-annotated-image')
export class AnnotatedImage extends LitElement implements AnnotatedImageAttributes {
    static styles = css`
        :host {
            display: flex;
            position: relative;
            /* default to size of image */
            width: max-content;
            /* center image if this component doesn't match the image aspect ratio */
            justify-content: center;
            align-items: center;
        }

        .image {
            width: var(--AnnotatedImage-width, auto);
            height: var(--AnnotatedImage-height, auto);
            max-width: var(--AnnotatedImage-max-width, 100%);
            max-height: var(--AnnotatedImage-max-height, 100%);

            /**
             * Maintain aspect ratio, assuming the annotation and image share the same
             * aspect ratio. If they don't they will be aligned to each others center
             * point.
             */
            object-fit: var(--AnnotatedImage-objectFit, contain);
        }

        .annotation {
            position: absolute;
            top: 0;
            left: 0;

            /* stretch overlay to fit the image */
            width: 100%;
            height: 100%;

            /* allow users to interact with underlying image by right clicking */
            pointer-events: none;
            object-fit: var(--AnnotatedImage-objectFit, contain);
            transform: var(--AnnotatedImage-annotation-transform, none);
        }

        .tile {
            width: 100%;
            margin: 0;
            max-height: 100%;
            max-width: 100%;
            display: block;
        }

        .is-loading {
            visibility: hidden;
            width: var(--AnnotatedImage-width, 4rem);
            height: var(--AnnotatedImage-height, 4rem);
        }
    `

    @property({ reflect: true })
    src: string

    @property({ reflect: true })
    tile: string

    @property({ reflect: true })
    annotation?: string

    @state()
    loading = false

    // Handling exponential backoff for image loading
    // If the image fails to load, it may be beause it doesn't exist
    // yet, so we retry automatically. We retry 4 times, with backoff
    // so that will take around 30 seconds to give up - in line with a
    // network timeout.
    private retries = 0

    private backoffTimer: ReturnType<typeof setTimeout>

    private onLoad = () => {
        this.retries = 0
        this.loading = false
    }

    private onError = () => {
        if (this.retries > MAX_RETRIES) {
            this.loading = false
            return
        }

        // If we have errored, place into loading state to hide the
        // broken image icon, and retry after a backoff period.

        this.loading = true
        const backoff = Math.pow(2, this.retries) * 1000
        this.retries += 1

        this.backoffTimer = setTimeout(() => {
            const url = new URL(this.src)
            url.hash = Date.now().toString() // bust cache to force reload
            this.src = url.toString()
        }, backoff)
    }

    connectedCallback() {
        super.connectedCallback()
        this.retries = 0
    }

    disconnectedCallback() {
        super.disconnectedCallback()
        clearTimeout(this.backoffTimer)
    }

    render() {
        return html`
            <img
                class=${classNames('image', { tile: this.tile === 'true', 'is-loading': this.loading })}
                src=${this.src}
                @load=${this.onLoad}
                @error=${this.onError}
            />
            ${this.annotation ? html`<img class="annotation" src=${this.annotation} /> ` : nothing}
        `
    }
}
